import { TestMarke } from "@/components/TestMarke";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Download, Trash2, Edit, CheckCircle, Search, Info } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, Badge } from "@/components/common";
import { nummerPasst } from "@/lib/nummernsuche";
import { api, API } from "@/lib/api";
import { DocumentPreview } from "@/components/DocumentPreview";
import { belegLohnPruefen } from "@/lib/lohnFrage";
import { angebotFestschreiben } from "@/lib/angebotFestschreiben";

const QuotesPage = ({ suche, setSuche } = {}) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  // 07.10.2026: Suchbegriff kann von der Dokumente-Seite kommen (gemeinsam über alle Reiter)
  const [eigeneSuche, setEigeneSuche] = useState("");
  const searchTerm = suche ?? eigeneSuche;
  const setSearchTerm = setSuche || setEigeneSuche;
  const [statusFilter, setStatusFilter] = useState("alle");
  const navigate = useNavigate();
  const statusCounts = useMemo(() => {
    const c = { alle: quotes.filter(q => q.status !== "Beauftragt").length };
    ["Entwurf", "Gesendet", "Beauftragt", "Abgelehnt"].forEach((s) => { c[s] = quotes.filter(q => q.status === s).length; });
    return c;
  }, [quotes]);

  const filteredQuotes = useMemo(() => {
    // Weitergewandelte Angebote (Status "Beauftragt") nicht mehr in der Angebote-Liste anzeigen
    let offen = statusFilter === "alle" ? quotes.filter(q => q.status !== "Beauftragt") : quotes.filter(q => q.status === statusFilter);
    if (!searchTerm.trim()) return offen;
    // 07.10.2026: beim Suchen auch beauftragte Angebote finden (Nummernsuche)
    if (statusFilter === "alle") offen = quotes;
    const term = searchTerm.toLowerCase();
    return offen.filter(q =>
      (q.betreff || "").toLowerCase().includes(term) ||
      (q.customer_name || "").toLowerCase().includes(term) ||
      (q.quote_number || "").toLowerCase().includes(term) || nummerPasst(q.quote_number, searchTerm)
    );
  }, [quotes, searchTerm, statusFilter]);

  useEffect(() => {
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try {
      const res = await api.get("/quotes");
      setQuotes(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (err) {
      toast.error("Fehler beim Laden der Angebote");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      setTimeout(() => setConfirmDeleteId(null), 3000);
      return;
    }
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Angebot gelöscht");
      setConfirmDeleteId(null);
      loadQuotes();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleDownloadPDF = async (id, number, e) => {
    e?.stopPropagation();
    if (!(await belegLohnPruefen("quote", id, () => navigate(`/quotes/edit/${id}`)))) return;   // BUT-0030: Lohnanteil bestätigen
    // Download zählt als Versand: "Gültig bis" vorher festschreiben
    try {
      await angebotFestschreiben(id);
    } catch {
      toast.error("Angebot konnte nicht festgeschrieben werden");
      return;
    }
    loadQuotes();
    try {
      const res = await api.get(`/pdf/quote/${id}`, { responseType: "blob", params: { download: true } });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Angebot_${number}.pdf`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 200);
      toast.success("PDF heruntergeladen");
    } catch (err) {
      toast.error("Fehler beim PDF-Download");
    }
  };

  const handleCreateOrder = async (quoteId, e) => {
    e?.stopPropagation();
    try {
      await api.post(`/orders/from-quote/${quoteId}`);
      toast.success("Auftrag erstellt");
      loadQuotes();
    } catch (err) {
      toast.error("Fehler beim Erstellen des Auftrags");
    }
  };

  const handleEdit = (quote, e) => {
    e?.stopPropagation();
    if (quote.status !== "Entwurf") {
      const weiter = window.confirm(
        `Dieses Angebot wurde bereits versendet oder bearbeitet (Status: ${quote.status}). Möchten Sie es wirklich bearbeiten? Ein erneutes Bearbeiten kann dazu führen, dass eine veränderte Version erneut verschickt wird.`
      );
      if (!weiter) return;
    }
    navigate(`/quotes/edit/${quote.id}`);
  };

  const getStatusBadge = (status) => {
    const variants = {
      Entwurf: "warning",
      Gesendet: "info",
      Beauftragt: "success",
      Abgelehnt: "danger"
    };
    return <Badge variant={variants[status] || "default"}>{status}</Badge>;
  };

  return (
    <div data-testid="quotes-page">
      <div className="flex items-center justify-between mb-4 lg:mb-8">
        <div>
          <h1 className="text-2xl lg:text-4xl font-bold">Angebote</h1>
          <p className="text-muted-foreground mt-1 text-sm lg:text-base">{quotes.length} Angebote gesamt</p>
        </div>
        <Button data-testid="btn-new-quote" onClick={() => navigate("/quotes/new")} size="sm" className="lg:h-10 lg:px-4">
          <Plus className="w-4 h-4 lg:w-5 lg:h-5" />
          <span className="hidden sm:inline">Neues Angebot</span>
          <span className="sm:hidden">Neu</span>
        </Button>
      </div>

      {/* Status-Filter */}
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="quotes-status-filter">
        {[
          { key: "alle", label: "Alle", info: "Alle Angebote ausser Bestätigt" },
          { key: "Entwurf", label: "Entwurf", info: "Wartet auf Fertigstellung und Versenden" },
          { key: "Gesendet", label: "Gesendet", info: "Gesendet, aber noch nicht beauftragt" },
          { key: "Beauftragt", label: "Bestätigt", info: "Kunde hat beauftragt" },
          { key: "Abgelehnt", label: "Abgelehnt", info: "Vom Kunden abgelehnt" },
        ].map((s) => {
          const count = statusCounts[s.key] || 0;
          return (
            <button key={s.key} type="button" onClick={() => setStatusFilter(s.key)} title={s.info} data-testid={`quotes-filter-${s.key}`} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${statusFilter === s.key ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}>
              {s.label}
              <Info className="w-3 h-3 text-blue-500" />
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${statusFilter === s.key ? "bg-primary-foreground/20" : "bg-muted"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Suchfeld */}
      <div className="relative mb-4" data-testid="quotes-search">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Suchen nach Beschreibung, Kunde oder Nr..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border rounded-sm text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="input-search-quotes"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : quotes.length === 0 ? (
        <Card className="p-8 lg:p-12 text-center">
          <FileText className="w-10 h-10 lg:w-12 lg:h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-base lg:text-lg font-semibold">Keine Angebote vorhanden</h3>
          <p className="text-muted-foreground mt-2 text-sm">Erstellen Sie Ihr erstes Angebot</p>
        </Card>
      ) : (
        <>
          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {filteredQuotes.map((quote) => (
              <Card key={quote.id} className="p-4" onClick={() => setPreviewQuote(quote)}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-sm text-muted-foreground">{quote.quote_number} {quote.uebungsbeleg && <TestMarke />}</p>
                    <p className="font-semibold truncate">{quote.customer_name}</p>
                    {quote.betreff && <p className="text-sm text-muted-foreground truncate mt-0.5">{quote.betreff}</p>}
                  </div>
                  {getStatusBadge(quote.status)}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-sm text-muted-foreground">
                    {new Date(quote.created_at).toLocaleDateString("de-DE")}
                  </div>
                  <div className="font-mono font-semibold">
                    {quote.total_gross.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                  </div>
                </div>
                <div className="flex justify-end gap-1 mt-3 pt-3 border-t">
                  <button data-testid={`btn-edit-quote-${quote.id}`} onClick={(e) => handleEdit(quote, e)} className="p-2 hover:bg-muted rounded-sm"><Edit className="w-4 h-4" /></button>
                  <button data-testid={`btn-download-quote-m-${quote.id}`} onClick={(e) => handleDownloadPDF(quote.id, quote.quote_number, e)} className="p-2 hover:bg-muted rounded-sm"><Download className="w-4 h-4" /></button>
                  {quote.status === "Entwurf" && (
                    <button onClick={(e) => handleCreateOrder(quote.id, e)} className="p-2 hover:bg-primary/10 text-primary rounded-sm"><CheckCircle className="w-4 h-4" /></button>
                  )}
                  <button onClick={(e) => handleDelete(quote.id, e)} className={`p-2 rounded-sm transition-colors ${confirmDeleteId === quote.id ? 'bg-red-500 text-white' : 'hover:bg-destructive/10 hover:text-destructive'}`}>
                    {confirmDeleteId === quote.id ? <span className="text-xs font-bold px-1">Löschen?</span> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop Table */}
          <Card className="hidden lg:block">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-4 font-semibold">Nr.</th>
                  <th className="text-left p-4 font-semibold">Kunde</th>
                  <th className="text-left p-4 font-semibold">Beschreibung</th>
                  <th className="text-left p-4 font-semibold">Datum</th>
                  <th className="text-right p-4 font-semibold">Betrag</th>
                  <th className="text-left p-4 font-semibold">Status</th>
                  <th className="text-right p-4 font-semibold">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuotes.map((quote) => (
                  <tr 
                    key={quote.id} 
                    className="border-b table-row-hover cursor-pointer"
                    onClick={() => setPreviewQuote(quote)}
                  >
                    <td className="p-4 font-mono text-sm">{quote.quote_number} {quote.uebungsbeleg && <TestMarke />}</td>
                    <td className="p-4">{quote.customer_name}</td>
                    <td className="p-4 text-muted-foreground text-sm max-w-[250px] truncate">{quote.betreff || "-"}</td>
                    <td className="p-4 text-muted-foreground">
                      {new Date(quote.created_at).toLocaleDateString("de-DE")}
                    </td>
                    <td className="p-4 text-right font-mono">
                      {quote.total_gross.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                    </td>
                    <td className="p-4">{getStatusBadge(quote.status)}</td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button
                          data-testid={`btn-edit-quote-${quote.id}`}
                          onClick={(e) => handleEdit(quote, e)}
                          className="p-2 hover:bg-muted rounded-sm"
                          title="Bearbeiten"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`btn-download-quote-${quote.id}`}
                          onClick={(e) => handleDownloadPDF(quote.id, quote.quote_number, e)}
                          className="p-2 hover:bg-muted rounded-sm"
                          title="PDF herunterladen"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {quote.status === "Entwurf" && (
                          <button
                            data-testid={`btn-create-order-${quote.id}`}
                            onClick={(e) => handleCreateOrder(quote.id, e)}
                            className="p-2 hover:bg-primary/10 text-primary rounded-sm"
                            title="Auftrag erstellen"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          data-testid={`btn-delete-quote-${quote.id}`}
                          onClick={(e) => handleDelete(quote.id, e)}
                          className={`p-2 rounded-sm transition-colors ${confirmDeleteId === quote.id ? 'bg-red-500 text-white' : 'hover:bg-destructive/10 hover:text-destructive'}`}
                          title={confirmDeleteId === quote.id ? "Nochmal klicken zum Löschen" : "Löschen"}
                        >
                          {confirmDeleteId === quote.id ? <span className="text-xs font-bold px-1">Löschen?</span> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        </>
      )}

      <DocumentPreview
        isOpen={!!previewQuote}
        onClose={() => setPreviewQuote(null)}
        document={previewQuote}
        type="quote"
        onDownload={(id, num) => handleDownloadPDF(id, num)}
        onEdit={(q) => handleEdit(q)}
        onFestgeschrieben={() => loadQuotes()}
      />
    </div>
  );
};


export { QuotesPage };
